import { faRotate, faSpinner } from "@fortawesome/pro-duotone-svg-icons";
import { faBook } from "@fortawesome/pro-regular-svg-icons";
import { faArrowsMinimize, faPenToSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DOMParser, Node as PMNode } from "@tiptap/pm/model";
import classNames from "classnames";
import { findTextNodes } from "prosemirror-utils";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Handle,
  Node,
  NodeResizer,
  Position,
  useNodeId,
  useStore,
  useUpdateNodeInternals,
} from "reactflow";
import pSBC from "shade-blend-color";
import { yXmlFragmentToProsemirrorJSON } from "y-prosemirror";

import { django } from "@/api";
import { convertXmlToYjs } from "@/extensions/RoadmapList/xmlToYjsConverter";
import { useActiveEditor, useQueries, useYdoc } from "@/hooks";
import usePages from "@/hooks/usePages";

import styles from "./Graph.module.scss";

const GraphNode = ({
  id,
  data,
  selected,
  readOnly = false,
  ...props
}: {
  id: string;
  data: any;
  selected: boolean;
  readOnly?: boolean;
  props: Node;
}) => {
  const nodeId = useNodeId();
  const updateNodeInternals = useUpdateNodeInternals();
  const { currentProject } = useQueries();
  const { nodesMap } = useYdoc();
  const { activeEditor } = useActiveEditor();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const navigate = useNavigate();
  const nodeRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  const isPaperNode = Boolean(data?.paper?.paperId);

  useEffect(() => {
    if (!inputRef.current || isFocused) return;
    inputRef.current.innerHTML = data.label;
  }, [inputRef]);

  useEffect(() => {
    if (!inputRef.current || isFocused) return;
    inputRef.current.innerHTML = data.label;
  }, [inputRef, data.label, data.loading]);

  useEffect(() => {
    if (isFocused === false) {
      setIsEditing(false);
    }
  }, [isFocused]);

  const handle = {
    borderWidth: "3px",
    borderColor: "#fff",
    width: 10,
    height: 10,
  };

  const nodeClasses = classNames(styles.node, {
    GraphNode: true,
    [styles.isEditing]: isEditing,
    [styles.hasColorBorder]: Boolean(data.borderColor),
    ["nopan"]: readOnly,
    [styles.selected]: selected,
    [styles.hasChildren]: data?.hasChildren,
  });

  const nodeStyle = { borderColor: "#000", background: "", opacity: 1 };
  if (data.borderColor) nodeStyle.borderColor = data.borderColor;
  // if (selected) nodeStyle.borderColor = "red";
  if (data?.progress)
    nodeStyle.background = `linear-gradient(to right, ${
      data?.borderColor ? pSBC(0.9, data?.borderColor) : "#eeeeee"
    } ${data?.progress}%, white ${data?.progress}%)`;
  if (data?.loading) nodeStyle.opacity = 0.8;

  const [lineClamp, setLineClamp] = useState<number>(3);

  useEffect(() => {
    if (!nodeRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setLineClamp(Math.floor((nodeRef?.current?.clientHeight ?? 0) / 19) - 1);
    });
    resizeObserver.observe(nodeRef.current);
    return () => resizeObserver.disconnect(); // clean up
  }, [nodeRef?.current]);

  const onInput = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      let targetPos = null;
      activeEditor?.state.doc.descendants((node, pos) => {
        if (node.attrs.blockId && node.attrs.blockId === nodeId && node.type.name == "listItem") {
          targetPos = pos;
          return false;
        }
      });

      // If the node was found
      if (targetPos !== null && activeEditor?.schema) {
        // Determine the position of the node's text content
        const targetNode = activeEditor?.state.doc.nodeAt(targetPos);
        const textNode = targetNode?.content.firstChild;
        const from = targetPos + 1;
        if (!textNode) return;
        const to = from + textNode.nodeSize - 1;

        const parser = DOMParser.fromSchema(activeEditor?.schema);
        const prosemirrorNodes = parser.parse(e.currentTarget);

        // Create a transaction to replace the old text with the new text
        const transaction = activeEditor?.state.tr.replaceWith(from, to, prosemirrorNodes);
        if (transaction) activeEditor?.view.dispatch(transaction);
      }
    },
    [activeEditor]
  );

  const onClickEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isEditing == false) {
      setTimeout(function () {
        if (!inputRef.current) return;
        var range = document.createRange();
        var sel = window.getSelection();
        range.selectNodeContents(inputRef.current);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
        inputRef.current.focus();
      }, 0);
    }
    setIsEditing(!isEditing);
  };

  const onClickCollapse = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const oldNode = nodesMap?.get(id);
      if (!oldNode) return;
      nodesMap?.set(id, {
        ...oldNode,
        style: { width: 200, height: 80 },
        data: { ...oldNode.data, collapsed: !oldNode?.data.collapsed },
      });
    },
    [nodesMap]
  );

  const onDoubleClick = (event: React.MouseEvent) => {
    if (isEditing) return;
    navigate(`/project/${currentProject?.slug}/${id}`);
  };

  return (
    <>
      {!readOnly && (
        <NodeResizer
          color="transparent"
          isVisible={true}
          minWidth={100}
          minHeight={30}
          lineStyle={{ borderWidth: 5 }}
          handleStyle={{ borderWidth: 5, borderColor: "transparent" }}
        />
      )}
      <Handle id="target-top" type="target" position={Position.Top} style={handle} />
      <Handle id="target-left" type="target" position={Position.Left} style={handle} />
      <div className={nodeClasses} style={nodeStyle} ref={nodeRef} onDoubleClick={onDoubleClick}>
        {data.collapsed && (
          <div className={styles.collapsedIcon}>
            <FontAwesomeIcon icon={faArrowsMinimize} size="xs" title="Collapsed" />
          </div>
        )}
        {isPaperNode && (
          <button className={styles.paperIcon}>
            <FontAwesomeIcon icon={faBook} size="xs" />
          </button>
        )}
        {!readOnly && !isEditing && !isPaperNode && (
          <button onClick={onClickCollapse} className={styles.collapseButton}>
            <FontAwesomeIcon icon={faArrowsMinimize} size="xs" />
          </button>
        )}
        {!readOnly && !isEditing && !isPaperNode && (
          <button onClick={onClickEdit} className={styles.editButton}>
            <FontAwesomeIcon icon={faPenToSquare} size="xs" /> Edit
          </button>
        )}
        {data?.loading === true ? (
          <div className={styles.nodeLabel}>Loading...</div>
        ) : (
          <div
            contentEditable={!readOnly && isEditing}
            onInput={onInput}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            ref={inputRef}
            className={classNames(styles.nodeLabel, styles[`lineClamp-${lineClamp}`], {
              nodrag: isEditing,
              [styles.contentEditable]: isEditing,
              [styles.readOnly]: readOnly,
            })}
          ></div>
        )}
        {Boolean(selected && data?.tokens) && (
          <div className={styles.tokens}>{data?.tokens} tokens</div>
        )}
      </div>
      <Handle id="target-bottom" type="source" position={Position.Bottom} style={handle} />
      <Handle id="target-right" type="source" position={Position.Right} style={handle} />
    </>
  );
};

export default memo(GraphNode);
